import { ApiPaymentSettingList } from '@/models/api/payment/setting'
import BehaviorConfig from '@/models/app-config/behavior/behavior'
import { PaymentType } from '@/models/app-config/behavior/types'
import { InquiryKind } from '@spa/components/organisms/inquiry/model/InquiryKind'
import _ from 'lodash'

export function createInquiryKindRepository(behaviorConfig: BehaviorConfig): InquiryKindRepository {
  if (behaviorConfig.isLikeHotellovers) {
    return {
      listForHost: deps => Promise.resolve(insertPleaseSelectDecorator([
        {
          value: 'order',
          text: '注文',
          details: [
            { value: 'cancel', text: 'キャンセル・注文内容の変更について' },
            { value: 'etc', text: 'その他' },
          ],
        },
        {
          value: 'logistics',
          text: '配送',
          details: [
            { value: 'destination_change', text: '配送先変更' },
            { value: 'etc', text: 'その他' },
          ],
        },
        commonInquiryKinds.payment(deps),
        {
          value: 'system',
          text: 'システム／ログイン・パスワード',
          details: [
            { value: 'register', text: '会員登録・ログインについて' },
            { value: 'mailmagazine', text: 'メールマガジンの停止・再開について' },
            { value: 'password', text: 'パスワードについて' },
            { value: 'deleting_account', text: '退会について' },
            { value: 'etc', text: 'その他' },
          ],
        },
        {
          value: 'event',
          text: 'キャンペーン／クーポン／ポイント',
          details: [
            { value: 'coupon', text: 'クーポン' },
            { value: 'campaign', text: 'キャンペーン' },
            { value: 'point', text: 'ポイント' },
            { value: 'etc', text: 'その他' },
          ],
        },
        commonInquiryKinds.etc(),
      ])),
    }
  }

  return {
    listForHost: deps => Promise.resolve([
      {
        value: 'template',
        text: 'お問い合わせご返答について',
        details: [
          { value: 'mail_delay', text: 'メール遅延' },
          { value: 'repeat', text: '繰り返し' },
        ],
      },
      {
        value: 'order',
        text: '注文',
        details: [
          { value: 'order_method_unknown', text: '注文方法がわからない' },
          { value: 'cancel', text: 'キャンセル' },
          { value: 'change_order', text: '注文内容変更' },
          { value: 'etc', text: 'その他' },
        ],
      },
      {
        value: 'logistics',
        text: '配送',
        details: [
          { value: 'date_change', text: '日時指定・保管期限変更' },
          { value: 'delivery_info', text: 'お届け目安' },
          { value: 'destination_change', text: '配送先変更' },
          { value: 'redelivery_request', text: '再発送依頼' },
          { value: 'delivery_method_change', text: '配送方法変更' },
          { value: 'damaged_on_delivery', text: '配送時破損' },
          { value: 'etc', text: 'その他' },
        ],
      },
      {
        ...commonInquiryKinds.payment(deps),
        details: _.compact([
          ...commonInquiryKinds.payment(deps).details,
        ]),
      },
      {
        value: 'return_exchange',
        text: '返品・交換',
        details: [
          {
            value: 'damaged_goods',
            text: '不良品について',
            defaultContent: buildDefaultContentForReturnExchange(behaviorConfig),
          },
          {
            value: 'customer_opinion',
            text: 'お客様都合（サイズ・イメージ）',
            defaultContent: buildDefaultContentForReturnExchange(behaviorConfig),
          },
          { value: 'etc', text: 'その他', defaultContent: buildDefaultContentForReturnExchange(behaviorConfig) },
        ],
      },
      commonInquiryKinds.system(),
      {
        value: 'event',
        text: '催事・店舗',
        details: [
          { value: 'store', text: '店舗' },
          { value: 'point_coupon', text: 'ポイント・クーポン' },
          { value: 'novelty', text: 'ノベルティ' },
          { value: 'gift', text: 'ギフト' },
          { value: 'paper_bag', text: 'ショッパーについて' },
          { value: 'catalog', text: 'カタログ' },
          { value: 'etc', text: 'その他' },
        ],
      },
      commonInquiryKinds.etc(),
    ]),
  }
}

/**
 * 問い合わせ項目を取得するための依存情報
 */
interface  InquiryKindListDeps {
  readonly paymentSettings: ApiPaymentSettingList
}
export interface InquiryKindRepository {
  listForHost(deps: InquiryKindListDeps): Promise<InquiryKind[]>
}

/**
 * 決済方法に関する問い合わせ項目を構築するために必要な依存情報
 */
type PaymentInquiryKindDeps = Pick<InquiryKindListDeps, 'paymentSettings'>

const insertPleaseSelectDecorator = (inquiryKinds: InquiryKind[]): InquiryKind[] => {

  const dummyDetail = { value: '', text: '選択してください', disabled: true }
  const dummyKind = {
    value: '',
    text: '選択してください',
    disabled: true,
    details: [
      { value: '', text: '選択してください', disabled: true  },
    ],
  }

  return [
    dummyKind,
    ...inquiryKinds.map(item =>
      item.details.length === 0 ? item : {
        ...item,
        details: [dummyDetail, ...item.details],
      }
    ),
  ]
}

/**
 * 業態に関わらず一定の問い合わせ項目
 */
const commonInquiryKinds = {
  system: (): InquiryKind => ({
    value: 'system',
    text: 'システム',
    details: [
      { value: 'mail', text: 'メール' },
      { value: 'register', text: '会員登録・ログインについて' },
      { value: 'etc', text: 'その他' },
    ],
  }),
  payment(deps: PaymentInquiryKindDeps): InquiryKind {
    const paymentIsSelectable = (paymentId: PaymentType) =>
      deps.paymentSettings.some(setting => setting.id === paymentId)

    return {
      value: 'payment',
      text: '決済',
      details: _.compact([
        /**
         * /api/common/payment/settingに応じて動的に追加/削除できるようにしたほうがよい
         */
        paymentIsSelectable('creditcard') || paymentIsSelectable('creditcard3d')
          ? { value: 'creditcard', text: 'クレジットカード' }
          : null,
        paymentIsSelectable('np-postpay') ? { value: 'np', text: 'NP後払い' } : null,
        paymentIsSelectable('atone') ? { value: 'atone', text: 'atone／翌月まとめてコンビニ後払い' } : null,
        paymentIsSelectable('paidy') ? { value: 'paidy', text: 'Paidy' } : null,
        paymentIsSelectable('webmoney') ? { value: 'paypay', text: 'PayPay' } : null,
        paymentIsSelectable('cash-on-delivery') ? { value: 'daibiki', text: '代金引換' } : null,
        paymentIsSelectable('cvs-number') ? { value: 'konbini', text: 'コンビニ決済（前払い）' } : null,
        paymentIsSelectable('carrier') ? { value: 'carrier', text: '携帯キャリア決済' } : null,
        { value: 'etc', text: 'その他' },
      ]),
    }
  },
  etc: (): InquiryKind => ({
    value: 'etc',
    text: 'その他',
    details: [
      { value: 'about_receipt', text: '領収書について' },
      { value: 'about_product', text: '商品情報について' },
      { value: 'etc', text: 'その他' },
    ],
  }),
}

export function buildDefaultContentForReturnExchange(behaviorConfig: BehaviorConfig): string {
  if (behaviorConfig.isMyColor) {
    return `(1) 商品の受取日：
(2) 商品名・品番・カラー・サイズ・数量：
(3) 商品の状態：〈未使用・試着のみ・使用済み〉
(4) 洗濯の有無：
(5) タグ・下げ札：〈そのまま・外して保管・破棄済〉
(6) 箱・袋等の備品の有無：
(7) 希望対応：〈返品・交換〉
(8) 理由：〈記載例：サイズ・イメージ・不良 など〉
(9)不良箇所の詳細：〈記載例：右肩糸ほつれ1箇所 など〉
(10)写真添付〈商品全体・不備箇所アップ・靴裏〉`
  }

  if (behaviorConfig.isLikeHotellovers) {
    return `(1) 対象商品の到着日：※運送業者にてお届けされた日をご記載ください。
(2) 対象商品名・品番：
(3) 数量：
(4) 対象の商品：カラコン・カラコン以外
(5) 装用期間：ワンデー・2ウィーク・マンスリー・その他
(6) カラー：
(7) 度数：
(8) ご希望の対応：　返品・交換・その他
(9) お問い合わせ詳細：※お問い合わせはできるだけ詳細に記載頂きますようお願いいたします。
(10)保管枚数：
※現在お手元にございます保管枚数をご記載頂きますようお願い致します。
※レンズをご返送頂く際には枚数を確認しておりますのでご協力お願い致します。
(11)不具合商品の保管：あり・なし(破棄済み)
※破棄済みの場合は、返品交換いずれも対応ができかねます。`
  }

  if (behaviorConfig.isFitowa) {
    return `(1) 対象商品の到着日：
(2) 対象商品名・品番：
(3) 数量
(4) 対象商品の状態：　使用枚数　開封・未開封など
(5) タグ・値札の有無：
(6) 備品の有無：
(7) ご希望の対応：　返品・交換・その他
(8) 詳細情報：`
  }

  return `(1) 対象商品の到着日：
(2) 対象商品名・品番：
(3) 数量
(4) 対象商品の使用、着用状態：　使用＆着用なし、試着のみ、使用＆着用あり　など
(5) タグ・値札の有無：
(6) 備品の有無：
(7) ご希望の対応：　返品・交換・その他
(8) 詳細情報：`
}

export interface DetailItem {
  value: string
  text: string
}
